import React, { useState, useEffect } from 'react';
import Navbar from '../components/navBar';
import axios from 'axios';
import { ChartBarIcon, TrophyIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const STATS_OPTIONS = [
  'Table',
  'Goals',
  'Assists',
  'Saves',
  'Clearances',
  'Cards',
  'Shots on Target',
  'Penalty Miss',
  'Top 10 Players',
];

function PlayerAvatar({ player }) {
  const [imageFailed, setImageFailed] = useState(false);
  const name = player?.name || 'Player';
  const initials = name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line-strong bg-surface-800">
      {player?.profileImageUrl && !imageFailed ? (
        <img src={player.profileImageUrl} alt="" loading="lazy" onError={() => setImageFailed(true)} className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden="true" className="text-[10px] font-semibold text-gray-400">{initials || '?'}</span>
      )}
    </span>
  );
}

const Stats = () => {
  const [leagues, setLeagues] = useState([]);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [leaguesError, setLeaguesError] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [activeStatsOption, setActiveStatsOption] = useState(STATS_OPTIONS[0]);
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [goalsError, setGoalsError] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [clubsError, setClubsError] = useState(null);
  const [assists, setAssists] = useState([]);
  const [loadingAssists, setLoadingAssists] = useState(false);
  const [assistsError, setAssistsError] = useState(null);
  const [saves, setSaves] = useState([]);
  const [loadingSaves, setLoadingSaves] = useState(false);
  const [savesError, setSavesError] = useState(null);
  const [clearances, setClearances] = useState([]);
  const [loadingClearances, setLoadingClearances] = useState(false);
  const [clearancesError, setClearancesError] = useState(null);
  const [shotsOnTarget, setShotsOnTarget] = useState([]);
  const [loadingShotsOnTarget, setLoadingShotsOnTarget] = useState(false);
  const [shotsOnTargetError, setShotsOnTargetError] = useState(null);
  const [penaltyMisses, setPenaltyMisses] = useState([]);
  const [loadingPenaltyMisses, setLoadingPenaltyMisses] = useState(false);
  const [penaltyMissesError, setPenaltyMissesError] = useState(null);
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loadingStandings, setLoadingStandings] = useState(false);
  const [standingsError, setStandingsError] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [loadingTopPlayers, setLoadingTopPlayers] = useState(false);
  const [topPlayersError, setTopPlayersError] = useState(null);
  const currentYear = new Date().getFullYear();
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem('desac-sidebar-collapsed') === 'true'
  );

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem('desac-sidebar-collapsed') === 'true');
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === 'boolean'
          ? event.detail.collapsed
          : window.localStorage.getItem('desac-sidebar-collapsed') === 'true'
      );
    };
    window.addEventListener('resize', updateViewport);
    window.addEventListener('storage', updateCollapsedState);
    window.addEventListener('desac-sidebar-toggle', updateCollapsedState);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('storage', updateCollapsedState);
      window.removeEventListener('desac-sidebar-toggle', updateCollapsedState);
    };
  }, []);

  useEffect(() => {
    if (leagues.length === 0) fetchLeagues();
    if (clubs.length === 0) fetchClubs();
  }, []);

  useEffect(() => {
    if (selectedLeague && activeStatsOption === 'Goals') {
      setLoadingGoals(true);
      setGoalsError(null);
      fetch(`${API}/scores/leagues/${selectedLeague.leagueId}/goals`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch goals');
          return res.json();
        })
        .then((data) => setGoals(data))
        .catch((err) => setGoalsError(err.message))
        .finally(() => setLoadingGoals(false));
    }
    if (selectedLeague && activeStatsOption === 'Assists') {
      setLoadingAssists(true);
      setAssistsError(null);
      fetch(`${API}/scores/leagues/${selectedLeague.leagueId}/assists`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch assists');
          return res.json();
        })
        .then((data) => setAssists(data))
        .catch((err) => setAssistsError(err.message))
        .finally(() => setLoadingAssists(false));
    }
    if (selectedLeague && activeStatsOption === 'Saves') {
      setLoadingSaves(true);
      setSavesError(null);
      fetch(`${API}/saves/league/${selectedLeague.leagueId}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch saves');
          return res.json();
        })
        .then((data) => setSaves(data))
        .catch((err) => setSavesError(err.message))
        .finally(() => setLoadingSaves(false));
    }
    if (selectedLeague && activeStatsOption === 'Clearances') {
      setLoadingClearances(true);
      setClearancesError(null);
      fetch(`${API}/clearances/league/${selectedLeague.leagueId}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch clearances');
          return res.json();
        })
        .then((data) => setClearances(data))
        .catch((err) => setClearancesError(err.message))
        .finally(() => setLoadingClearances(false));
    }
    if (selectedLeague && activeStatsOption === 'Shots on Target') {
      setLoadingShotsOnTarget(true);
      setShotsOnTargetError(null);
      fetch(`${API}/shots-on-target/league/${selectedLeague.leagueId}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch shots on target');
          return res.json();
        })
        .then((data) => setShotsOnTarget(data))
        .catch((err) => setShotsOnTargetError(err.message))
        .finally(() => setLoadingShotsOnTarget(false));
    }
    if (selectedLeague && activeStatsOption === 'Penalty Miss') {
      setLoadingPenaltyMisses(true);
      setPenaltyMissesError(null);
      fetch(`${API}/penalty-misses/league/${selectedLeague.leagueId}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch penalty misses');
          return res.json();
        })
        .then((data) => setPenaltyMisses(data))
        .catch((err) => setPenaltyMissesError(err.message))
        .finally(() => setLoadingPenaltyMisses(false));
    }
    if (selectedLeague && activeStatsOption === 'Cards') {
      setLoadingCards(true);
      setCardsError(null);
      fetch(`${API}/cards/league/${selectedLeague.leagueId}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch cards');
          return res.json();
        })
        .then((data) => setCards(data))
        .catch((err) => setCardsError(err.message))
        .finally(() => setLoadingCards(false));
    }
    if (selectedLeague && activeStatsOption === 'Table') {
      setLoadingStandings(true);
      setStandingsError(null);
      fetch(`${API}/league-table/initialize/${selectedLeague.leagueId}/${currentYear}`, { method: 'POST' })
        .then(() => {
          return fetch(`${API}/league-table/standings/${selectedLeague.leagueId}/${currentYear}`);
        })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch standings');
          return res.json();
        })
        .then((data) => setStandings(data.data || []))
        .catch((err) => setStandingsError(err.message))
        .finally(() => setLoadingStandings(false));
    }
    if (activeStatsOption === 'Top 10 Players') {
      setLoadingTopPlayers(true);
      setTopPlayersError(null);
      fetch(`${API}/players/top`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch top players');
          return res.json();
        })
        .then((data) => setTopPlayers(data.data || []))
        .catch((err) => setTopPlayersError(err.message))
        .finally(() => setLoadingTopPlayers(false));
    }
  }, [selectedLeague, activeStatsOption]);

  const fetchLeagues = async () => {
    setLoadingLeagues(true);
    setLeaguesError(null);
    try {
      const response = await axios.get(`${API}/leagues`);
      const leagueData = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setLeagues(leagueData);
      if (leagueData.length > 0) {
        setSelectedLeague(leagueData[0]);
      }
    } catch (error) {
      setLeaguesError('Failed to fetch leagues');
    } finally {
      setLoadingLeagues(false);
    }
  };

  const fetchClubs = async () => {
    setLoadingClubs(true);
    setClubsError(null);
    try {
      const response = await axios.get(`${API}/clubs`);
      setClubs(Array.isArray(response.data) ? response.data : response.data?.data || []);
    } catch (error) {
      setClubsError('Failed to fetch clubs');
    } finally {
      setLoadingClubs(false);
    }
  };

  const getClub = (clubId) => clubs.find((c) => c.clubId === clubId);
  const getClubName = (clubId) => getClub(clubId)?.name || '-';
  const getClubLogo = (clubId) => getClub(clubId)?.logo || '';
  const getMatchTeams = (match) => {
    if (!match) return '-';
    const home = getClubName(match.homeTeamId);
    const away = getClubName(match.awayTeamId);
    return `${home} vs ${away}`;
  };

  // Helper to group and count by player for goals
  const getGoalsByPlayer = (goals) => {
    const map = new Map();
    goals.forEach((goal) => {
      const id = goal.scorer?.playerId;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          player: goal.scorer,
          clubId: goal.scorer.clubId,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for assists
  const getAssistsByPlayer = (assists) => {
    const map = new Map();
    assists.forEach((item) => {
      const id = item.assist?.playerId;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          player: item.assist,
          clubId: item.assist.clubId,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for saves
  const getSavesByPlayer = (saves) => {
    const map = new Map();
    saves.forEach((item) => {
      const id = item.Player?.playerId;
      if (!id) return;
      const club = item.Player?.Club;
      if (!map.has(id)) {
        map.set(id, {
          player: item.Player,
          club,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for clearances
  const getClearancesByPlayer = (clearances) => {
    const map = new Map();
    clearances.forEach((item) => {
      const id = item.Player?.playerId;
      if (!id) return;
      const club = item.Player?.Club;
      if (!map.has(id)) {
        map.set(id, {
          player: item.Player,
          club,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for shots on target
  const getShotsOnTargetByPlayer = (shots) => {
    const map = new Map();
    shots.forEach((item) => {
      const id = item.Player?.playerId;
      if (!id) return;
      const club = item.Player?.Club;
      if (!map.has(id)) {
        map.set(id, {
          player: item.Player,
          club,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for penalty misses
  const getPenaltyMissesByPlayer = (penaltyMisses) => {
    const map = new Map();
    penaltyMisses.forEach((item) => {
      const id = item.Player?.playerId;
      if (!id) return;
      const club = item.Player?.Club;
      if (!map.has(id)) {
        map.set(id, {
          player: item.Player,
          club,
          count: 1,
        });
      } else {
        map.get(id).count++;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  // Helper to group and count by player for cards
  const getCardsByPlayer = (cards) => {
    const map = new Map();
    cards.forEach((item) => {
      const id = item.Player?.playerId;
      if (!id) return;
      const club = item.Player?.Club;
      if (!map.has(id)) {
        map.set(id, {
          player: item.Player,
          club,
          yellow: 0,
          red: 0,
        });
      }
      if (item.cardType === 'yellow') map.get(id).yellow++;
      if (item.cardType === 'red') map.get(id).red++;
    });
    return Array.from(map.values()).sort((a, b) => (b.red + b.yellow) - (a.red + a.yellow));
  };

  return (
    <>
      <Navbar />
      <main
        style={{ '--stats-sidebar-width': isDesktop && isCollapsed ? '5rem' : '16rem' }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--stats-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2"><ChartBarIcon aria-hidden="true" className="h-4 w-4" /> PERFORMANCE CENTRE</p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Sports statistics</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">Explore player rankings and competition performance from the available match data.</p>
            </div>
            <div aria-live="polite" className="inline-flex min-h-10 w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
              <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
              {selectedLeague?.name || (loadingLeagues ? 'Loading competitions…' : 'Select a competition')}
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          {/* Competition selector */}
          <section aria-label="Competition selection" className="mb-6 rounded-xl border border-line bg-surface-900 p-4 sm:p-5">
            <div className="mb-3 flex items-center gap-2">
              <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
              <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Competition</h2>
            </div>
            <div className="overflow-x-auto scrollbar-none" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex min-w-0 gap-2">
              {loadingLeagues ? (
                <div role="status" className="py-2 text-sm text-gray-400">Loading competitions…</div>
              ) : leaguesError ? (
                <div role="alert" className="flex flex-wrap items-center gap-3 py-2 text-sm text-accent-soft">
                  <span>{leaguesError}</span>
                  <button type="button" onClick={fetchLeagues} className="rounded-md px-2 py-1 font-semibold text-gray-200 underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">Try again</button>
                </div>
              ) : (
                leagues.map((league) => (
                  <button
                    key={league.leagueId}
                    onClick={() => {
                      setSelectedLeague(league);
                      setActiveStatsOption(STATS_OPTIONS[0]);
                    }}
                    type="button"
                    aria-pressed={selectedLeague?.leagueId === league.leagueId}
                    className={`min-h-10 shrink-0 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${selectedLeague?.leagueId === league.leagueId ? 'border-accent/30 bg-accent/10 text-accent-soft' : 'border-line bg-surface-850 text-gray-300 hover:bg-surface-800 hover:text-white active:bg-surface-700'}`}
                  >
                    {league.name}
                  </button>
                ))
              )}
              </div>
            </div>
          </section>

          {/* Statistics categories */}
          {selectedLeague && (
            <section aria-label="Statistics categories" className="mb-4 rounded-xl border border-line bg-surface-900 p-2 sm:p-3">
              <div role="tablist" aria-label="Statistics categories" className="flex gap-2 overflow-x-auto scrollbar-none">
                {STATS_OPTIONS.map((option) => (
                  <button
                    key={option}
                    onClick={() => setActiveStatsOption(option)}
                    type="button"
                    role="tab"
                    aria-selected={activeStatsOption === option}
                    aria-controls={`stats-panel-${option.toLowerCase().replace(/\s+/g, '-')}`}
                    id={`stats-tab-${option.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`min-h-10 shrink-0 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${activeStatsOption === option ? 'border-accent/30 bg-accent/10 text-white' : 'border-transparent text-gray-400 hover:border-line hover:bg-surface-850 hover:text-gray-200'}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Selected statistics */}
          {selectedLeague ? (
            <section role="tabpanel" aria-labelledby={`stats-tab-${activeStatsOption.toLowerCase().replace(/\s+/g, '-')}`} id={`stats-panel-${activeStatsOption.toLowerCase().replace(/\s+/g, '-')}`} className="min-w-0 rounded-xl border border-line bg-surface-900 p-4 sm:p-6 lg:p-8">
              <div className="mb-6 flex flex-col gap-3 border-b border-line pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="eyebrow-accent">{activeStatsOption === 'Table' ? 'COMPETITION OVERVIEW' : 'PLAYER PERFORMANCE'}</p>
                  <h2 className="mt-1 text-xl font-semibold text-white sm:text-2xl">{activeStatsOption === 'Top 10 Players' ? 'Top players' : activeStatsOption === 'Table' ? 'League table' : `${activeStatsOption} leaderboard`}</h2>
                </div>
                <span className="inline-flex w-fit items-center rounded-md border border-line bg-surface-850 px-2.5 py-1 text-xs font-medium text-gray-400">{selectedLeague.name}</span>
              </div>
              {activeStatsOption === 'Goals' ? (
                loadingGoals || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading goals...</div>
                ) : goalsError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{goalsError || clubsError}</div>
                ) : goals.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No goals found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Goals</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getGoalsByPlayer(goals).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">{getClubName(item.clubId)}</td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Assists' ? (
                loadingAssists || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading assists...</div>
                ) : assistsError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{assistsError || clubsError}</div>
                ) : assists.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No assists found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Assists</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getAssistsByPlayer(assists).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">{getClubName(item.clubId)}</td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Saves' ? (
                loadingSaves || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading saves...</div>
                ) : savesError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{savesError || clubsError}</div>
                ) : saves.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No saves found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Saves</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getSavesByPlayer(saves).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {item.club ? (
                                  <div className="flex items-center gap-2">
                                    {item.club.logo && (
                                    <img
                                      src={item.club.logo}
                                      alt=""
                                      loading="lazy"
                                      className="w-7 h-7 rounded-full object-cover border border-line-strong bg-surface-800"
                                    />
                                    )}
                                    <span className="text-gray-200 font-medium truncate max-w-[120px]">{item.club.name}</span>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Clearances' ? (
                loadingClearances || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading clearances...</div>
                ) : clearancesError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{clearancesError || clubsError}</div>
                ) : clearances.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No clearances found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Clearances</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getClearancesByPlayer(clearances).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {item.club ? (
                                  <div className="flex items-center gap-2">
                                    {item.club.logo && (
                                    <img
                                      src={item.club.logo}
                                      alt=""
                                      loading="lazy"
                                      className="w-7 h-7 rounded-full object-cover border border-line-strong bg-surface-800"
                                    />
                                    )}
                                    <span className="text-gray-200 font-medium truncate max-w-[120px]">{item.club.name}</span>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Shots on Target' ? (
                loadingShotsOnTarget || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading shots on target...</div>
                ) : shotsOnTargetError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{shotsOnTargetError || clubsError}</div>
                ) : shotsOnTarget.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No shots on target found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Shots on Target</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getShotsOnTargetByPlayer(shotsOnTarget).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {item.club ? (
                                  <span className="text-gray-200 font-medium truncate max-w-[120px]">{item.club.name}</span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Penalty Miss' ? (
                loadingPenaltyMisses || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading penalty misses...</div>
                ) : penaltyMissesError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{penaltyMissesError || clubsError}</div>
                ) : penaltyMisses.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No penalty misses found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Penalty Misses</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPenaltyMissesByPlayer(penaltyMisses).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {item.club ? (
                                  <div className="flex items-center gap-2">
                                    {item.club.logo && (
                                    <img
                                      src={item.club.logo}
                                      alt=""
                                      loading="lazy"
                                      className="w-7 h-7 rounded-full object-cover border border-line-strong bg-surface-800"
                                    />
                                    )}
                                    <span className="text-gray-200 font-medium truncate max-w-[120px]">{item.club.name}</span>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Cards' ? (
                loadingCards || loadingClubs ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading cards...</div>
                ) : cardsError || clubsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{cardsError || clubsError}</div>
                ) : cards.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No cards found for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[560px] w-full text-sm sm:text-base">
                        <caption className="sr-only">{activeStatsOption} rankings for {selectedLeague.name}</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-amber-300">Yellow Cards</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-accent-soft">Red Cards</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getCardsByPlayer(cards).map((item, idx) => (
                            <tr
                              key={item.player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={item.player} />
                                  <span className="text-white font-medium">{item.player?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {item.club ? (
                                  <span className="text-gray-200 font-medium truncate max-w-[120px]">{item.club.name}</span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-amber-300 tabular-nums">{item.yellow}</td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{item.red}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Table' ? (
                loadingStandings ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading table...</div>
                ) : standingsError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{standingsError}</div>
                ) : standings.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No standings data available for this league.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[720px] w-full text-sm sm:text-base">
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">Pos</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Club</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">P</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">W</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">D</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">L</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">GF</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">GA</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">GD</th>
                            <th scope="col" className="px-4 py-3 font-semibold text-gray-300">Pts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {standings.map((team, index) => (
                            <tr key={team.id ?? `${team.clubId ?? team.club?.clubId ?? 'club'}-${team.position ?? index}`} className="table-row group">
                              <td className="px-4 py-3 text-center font-medium text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${index === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{team.position ?? index + 1}</span></td>
                              <td className="px-4 py-3 text-gray-200">
                                <div className="flex items-center gap-3">
                                  {team.club?.logo && <img src={team.club.logo} alt="" loading="lazy" className="h-8 w-8 shrink-0 rounded-full border border-line-strong bg-surface-800 object-contain p-1" />}
                                  <span className="font-medium">{team.club?.name || '-'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.played}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.won}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.drawn}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.lost}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.goalsFor}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.goalsAgainst}</td>
                              <td className="px-4 py-3 text-center text-gray-400 tabular-nums">{team.goalDifference}</td>
                              <td className="px-4 py-3 text-center font-bold text-accent-soft tabular-nums">{team.points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : activeStatsOption === 'Top 10 Players' ? (
                loadingTopPlayers ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">Loading top players...</div>
                ) : topPlayersError ? (
                  <div role="alert" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-12 text-center text-accent-soft">{topPlayersError}</div>
                ) : topPlayers.length === 0 ? (
                  <div role="status" className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center text-gray-400">No top players data available.</div>
                ) : (
                  <div className="w-full">
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface-850">
                      <table className="min-w-[720px] w-full text-sm sm:text-base">
                        <caption className="sr-only">Top player statistics for all competitions</caption>
                        <thead>
                          <tr className="bg-surface-600">
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300 w-12">#</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Player</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Position</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Age</th>
                            <th scope="col" className="px-4 py-3 text-left font-semibold text-gray-300">Team</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Goals</th>
                            <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-300">Assists</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topPlayers.map((player, idx) => (
                            <tr
                              key={player.playerId}
                              className="table-row group"
                            >
                              <td className="px-4 py-3 text-left font-medium align-middle text-gray-400 tabular-nums"><span className={`inline-flex min-w-7 justify-center rounded-md px-1.5 py-1 text-xs font-semibold ${idx === 0 ? 'bg-accent/10 text-accent-soft' : 'text-gray-400'}`}>{idx + 1}</span></td>
                              <td className="px-4 py-3 text-left align-middle">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar player={player} />
                                  <span className="text-white font-medium">{player.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">{player.position}</td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">{player.age}</td>
                              <td className="px-4 py-3 text-left align-middle text-gray-400">
                                {player.Club ? (
                                  <div className="flex items-center gap-2">
                                    {player.Club.logo && (
                                      <img
                                        src={player.Club.logo}
                                        alt=""
                                        loading="lazy"
                                        className="w-7 h-7 rounded-full object-cover border border-line-strong bg-surface-800"
                                      />
                                    )}
                                    <span className="text-gray-200 font-medium truncate max-w-[120px]">{player.Club.name}</span>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{player.totalGoals}</td>
                              <td className="px-4 py-3 text-right font-bold align-middle text-accent-soft tabular-nums">{player.totalAssists}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : (
                <div className="rounded-lg border border-line bg-surface-850 px-4 py-12 text-center">
                  <h3 className="text-lg font-semibold text-white mb-2">{activeStatsOption}</h3>
                  <p className="text-sm leading-6 text-gray-400">
                    for <span className="font-bold text-white">{selectedLeague.name}</span> will be
                    displayed here.
                  </p>
                </div>
              )}
            </section>
          ) : !loadingLeagues && !leaguesError ? (
            <section className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <TrophyIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">No competitions available</h2>
              <p className="mt-2 text-sm text-gray-400">Statistics will appear once competitions are available.</p>
            </section>
          ) : null}
        </div>
      </main>
      {/* Hide scrollbars globally for these areas */}
      <style>{`
        .scrollbar-none::-webkit-scrollbar { display: none; }
        .scrollbar-none { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
    </>
  );
};

export default Stats; 
